import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getImageUrl } from '../utils/cdnUtils'
import { lotteryService, type LotteryRound } from '../services/lotteryService'

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.4 },
  }),
}

interface SetCardProps {
  round: LotteryRound
  index: number
  countdown: string
  navigate: NavigateFunction
}

const SetCard: React.FC<SetCardProps> = ({ round, index, countdown, navigate }) => {
  const isOpen = round.status === 'open' && round.isOpen
  const betEndDate = new Date(round.betEndTime)
  const closeTime = betEndDate.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <motion.div
      className="col-6 col-md-4 col-lg-3 px-1 mt-2"
      custom={index}
      initial="hidden"
      animate="visible"
      variants={cardVariants}
    >
      <a
        href="#"
        className={`${isOpen ? 'bgg-1' : 'bgc-4'} bdr-5 text-center box-hover h-100`}
        style={{ display: 'block', textDecoration: 'none' }}
        onClick={(e) => {
          e.preventDefault()
          if (isOpen) {
            navigate('/lottery-terms', { state: { round } })
          }
        }}
      >
        <div className={`px-1 py-1 t-regular ${isOpen ? 'c-white' : 'c-dark'}`}>
          <h5>{isOpen ? countdown : 'ปิดรับแทง'}</h5>
        </div>
        <div className="bgc-14 px-1 t-regular c-white">
          <h4 className="txt-hh">{round.huayName}</h4>
        </div>
        <div className={`px-1 py-2 ${isOpen ? 'c-white' : 'c-dark'}`}>
          <small>ปิดรับ {closeTime}</small>
        </div>
      </a>
    </motion.div>
  )
}

const SetPage: React.FC = () => {
  const navigate = useNavigate()
  const [rounds, setRounds] = useState<LotteryRound[]>([])
  const [loading, setLoading] = useState(true)
  const [countdown, setCountdown] = useState<Record<string, string>>({})

  const loadRounds = useCallback(async () => {
    try {
      setLoading(true)
      const todayRounds = await lotteryService.getTodayRounds()
      const setRoundsFiltered = todayRounds.filter(
        (round) =>
          round.huayTypeName?.includes('ชุด') || round.huayName?.includes('ชุด')
      )
      setRounds(setRoundsFiltered)
    } catch (error) {
      console.error('Error loading set lottery rounds:', error)
      setRounds([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRounds()
    const intervalId = setInterval(loadRounds, 30000)
    return () => clearInterval(intervalId)
  }, [loadRounds])

  useEffect(() => {
    if (rounds.length === 0) return
    const t = setInterval(() => {
      const next: Record<string, string> = {}
      rounds.forEach((r) => {
        next[r._id] = lotteryService.formatTimeRemaining(r.betEndTime)
      })
      setCountdown((prev) => ({ ...prev, ...next }))
    }, 1000)
    return () => clearInterval(t)
  }, [rounds])

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row mx--1">
          <div className="col-12 px-1">
            <div className="w-100 p-2 bgc-4 bdr-5 wrap justify-content-between">
              <a
                href="#"
                className="txt-link-d"
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/dashboard')
                }}
              >
                <i className="fas fa-chevron-left me-1"></i>
                หน้าหลัก
              </a>
            </div>
          </div>

          <div className="col-12 mt-2 px-1">
            <div className="bgc-14 bdr-5 p-2 h-100">
              <div className="wrap animateMe" data-animation="fadeInUp">
                <div className="icon-f">
                  <img src={getImageUrl('logo.png')} alt="" />
                </div>
                <h5 className="t-medium mx-2 c-white">
                  หวยชุด {!loading && `(${rounds.length} รอบ)`}
                </h5>
              </div>

              <div className="wrap-box">
                <div className="row mx--1">
                  {loading ? (
                    <div className="col-12 text-center py-5">
                      <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                      <p className="c-white mt-2">กำลังโหลดรอบหวยชุด...</p>
                    </div>
                  ) : rounds.length > 0 ? (
                    rounds.map((round, index) => (
                      <SetCard
                        key={round._id}
                        round={round}
                        index={index}
                        countdown={
                          countdown[round._id] ||
                          lotteryService.formatTimeRemaining(round.betEndTime)
                        }
                        navigate={navigate}
                      />
                    ))
                  ) : (
                    <div className="col-12 text-center py-5">
                      <p className="c-white">ไม่มีรอบหวยชุดในวันนี้</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SetPage
